import { useEffect, useState } from "react";
import { BarChart3, Eye, Mail, TrendingUp } from "lucide-react";
import { adminApi } from "../api";

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi
      .get(`/admin/analytics/summary?days=${days}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [days]);

  const maxCount = data?.by_day?.reduce((m, d) => Math.max(m, d.count), 0) || 1;

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex items-start justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
            CMS · Analytics
          </div>
          <h1 className="mt-2 font-display font-light text-5xl tracking-tightest">
            Visite & traffico.
          </h1>
          <p className="mt-3 text-white/60 max-w-xl">
            Statistiche di traffico del sito pubblico raccolte lato server.
            Dati aggregati, nessun cookie di profilazione.
          </p>
        </div>
        <div className="flex gap-2" data-testid="analytics-range-selector">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`analytics-range-${d}`}
              className={`px-4 py-2 rounded-full text-xs border transition-colors ${
                days === d
                  ? "bg-white text-black border-white"
                  : "border-white/15 text-white/60 hover:text-white"
              }`}
            >
              {d === 365 ? "1 anno" : `${d} giorni`}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="text-white/40 py-20 text-center">Caricamento…</div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            <KpiCard
              icon={<Eye size={16} />}
              label={`Visualizzazioni ultimi ${days} giorni`}
              value={data.pageviews_period.toLocaleString("it-IT")}
              testId="kpi-pageviews"
            />
            <KpiCard
              icon={<TrendingUp size={16} />}
              label="Totale storico"
              value={data.pageviews_all_time.toLocaleString("it-IT")}
              testId="kpi-all-time"
            />
            <KpiCard
              icon={<Mail size={16} />}
              label={`Contatti ricevuti (${days}gg)`}
              value={data.contacts_period.toLocaleString("it-IT")}
              testId="kpi-contacts"
            />
          </div>

          {/* By day bar chart */}
          <div className="rounded-2xl border border-white/10 bg-[#0F0F14] p-6 md:p-8 mb-10">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 size={14} className="text-[#C48F56]" />
              <h2 className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                Visite giornaliere
              </h2>
            </div>
            {data.by_day.length === 0 ? (
              <div className="py-12 text-center text-white/30 text-sm">
                Ancora nessun dato. Visita il sito pubblico per popolare il grafico.
              </div>
            ) : (
              <div
                className="flex items-end gap-1 h-48"
                data-testid="analytics-bars"
              >
                {data.by_day.map((d) => (
                  <div
                    key={d.day}
                    className="flex-1 group relative flex flex-col justify-end min-w-[4px]"
                    title={`${d.day} · ${d.count} visite`}
                  >
                    <div
                      className="bg-[#C48F56] hover:bg-white transition-colors rounded-t-sm"
                      style={{
                        height: `${(d.count / maxCount) * 100}%`,
                        minHeight: "2px",
                      }}
                    />
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-black px-2 py-1 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-white/10">
                      {d.day} · {d.count}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top paths + referrers */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-white/10 bg-[#0F0F14] p-6 md:p-8">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-5">
                Pagine più viste
              </h2>
              {data.top_paths.length === 0 ? (
                <div className="text-white/30 text-sm py-4">Nessun dato.</div>
              ) : (
                <div className="space-y-3" data-testid="analytics-top-paths">
                  {data.top_paths.map((p, i) => (
                    <div key={p.path} className="flex items-center gap-4">
                      <span className="font-mono text-[10px] text-white/30 w-6">
                        0{i + 1}
                      </span>
                      <span className="flex-1 truncate text-sm text-white/80">
                        {p.path}
                      </span>
                      <span className="font-mono text-xs text-[#C48F56] w-14 text-right">
                        {p.count}
                      </span>
                      <div className="w-24 h-1 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#C48F56]"
                          style={{
                            width: `${
                              (p.count / data.top_paths[0].count) * 100
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#0F0F14] p-6 md:p-8">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-5">
                Sorgenti di traffico
              </h2>
              {data.top_referrers.length === 0 ? (
                <div className="text-white/30 text-sm py-4">
                  Nessun referrer registrato. Quando i visitatori arrivano da
                  motori di ricerca o social, li vedrai qui.
                </div>
              ) : (
                <div className="space-y-3" data-testid="analytics-top-referrers">
                  {data.top_referrers.map((r, i) => (
                    <div key={r.referrer} className="flex items-center gap-4">
                      <span className="font-mono text-[10px] text-white/30 w-6">
                        0{i + 1}
                      </span>
                      <span className="flex-1 truncate text-sm text-white/80">
                        {shortDomain(r.referrer)}
                      </span>
                      <span className="font-mono text-xs text-[#C48F56]">
                        {r.count}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-white/10 bg-[#0F0F14] p-6"
    >
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-full bg-white/5 grid place-items-center text-[#C48F56]">
          {icon}
        </div>
      </div>
      <div className="mt-5 font-display text-5xl tracking-tight">{value}</div>
      <div className="mt-1 text-sm text-white/60">{label}</div>
    </div>
  );
}

function shortDomain(url) {
  try {
    const u = new URL(url);
    return u.hostname + (u.pathname !== "/" ? u.pathname : "");
  } catch {
    return url;
  }
}
